// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Box, Button, Group, Paper, Skeleton, Stack, Text, TextInput, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Appointment, ChargeItem, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, formatUsd, getPatientPaymentMethod } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { botIdentifier } from '../lib/bot-identifier';
import { AddCardModal } from './settings/AddCardModal';

const BRAND = '#640040';
const UNPAID_CHARGE_STATUSES = new Set<ChargeItem['status']>(['planned', 'billable']);

const couponMessages: Record<string, string> = {
  invalid_code: 'Coupon code not found',
  expired: 'This coupon has expired',
  expired_or_inactive: 'This coupon is no longer active',
  not_self_pay: 'This coupon is not available for your account',
  amount_too_low: 'Discount makes amount too low to charge',
  unsupported_discount_type: 'Coupon type not supported',
};

interface AppliedDiscountUi {
  code: string;
  percentOff: number;
  originalAmountCents: number;
  discountedAmountCents: number;
}

function isAppointmentPayable(appointment: Appointment, charge: ChargeItem | undefined): boolean {
  if (appointment.status === 'pending') {
    return true;
  }

  return appointment.status === 'booked' && !!charge && UNPAID_CHARGE_STATUSES.has(charge.status);
}

export function PayAppointmentPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;

  const [patient, setPatient] = useState<Patient>(profile);
  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [charge, setCharge] = useState<ChargeItem | undefined>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<AppliedDiscountUi | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => setPatient(profile), [profile]);

  useEffect(() => {
    if (!appointmentId) {
      return;
    }
    setLoading(true);
    Promise.all([
      medplum.readResource('Appointment', appointmentId),
      medplum.searchResources('ChargeItem', {
        identifier: `${APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM}|${appointmentId}`,
        _count: '1',
      }),
    ])
      .then(([appt, charges]) => {
        const appointmentCharge = charges[0] as ChargeItem | undefined;
        if (!isAppointmentPayable(appt as Appointment, appointmentCharge)) {
          navigate('/appointments', { replace: true });
          return;
        }
        setAppointment(appt as Appointment);
        setCharge(appointmentCharge);
      })
      .catch((err) => {
        Sentry.captureException(err);
        showNotification({ color: 'red', title: 'Could not load appointment', message: normalizeErrorString(err) });
      })
      .finally(() => setLoading(false));
  }, [medplum, appointmentId, navigate]);

  async function handleApplyCoupon() {
    if (!appointmentId || !code.trim()) {
      return;
    }
    setValidating(true);
    setCouponError(null);
    try {
      const result = (await medplum.executeBot(botIdentifier('validate-coupon'), {
        code: code.trim(),
        appointmentId,
      })) as
        | { valid: true; code: string; percentOff: number; originalAmountCents: number; discountedAmountCents: number }
        | { valid: false; reason: string };

      if (result.valid) {
        setApplied({
          code: result.code,
          percentOff: result.percentOff,
          originalAmountCents: result.originalAmountCents,
          discountedAmountCents: result.discountedAmountCents,
        });
      } else {
        const message = couponMessages[result.reason] ?? 'Could not apply coupon';
        setCouponError(message);
        showNotification({ color: 'red', title: 'Coupon error', message });
      }
    } catch (err) {
      Sentry.captureException(err);
      const message = normalizeErrorString(err);
      setCouponError(message);
      showNotification({ color: 'red', title: 'Coupon error', message });
    } finally {
      setValidating(false);
    }
  }

  function handleRemoveCoupon() {
    setApplied(null);
    setCode('');
    setCouponError(null);
  }

  async function handleConfirm() {
    if (!appointmentId) {
      return;
    }
    setSubmitting(true);
    try {
      await Sentry.startSpan({ op: 'ui.click', name: 'Confirm Appointment Payment' }, async () => {
        await medplum.executeBot(botIdentifier('confirm-appointment-payment'), {
          appointmentId,
          ...(applied ? { promotionCode: applied.code } : {}),
        });
      });
      showNotification({ color: 'green', title: 'Payment confirmed', message: 'Your appointment is booked.' });
      navigate('/appointments');
    } catch (err) {
      Sentry.captureException(err);
      const message = normalizeErrorString(err);
      if (applied && /Coupon rejected/.test(message)) {
        setApplied(null);
        setCouponError('Coupon was revoked. Try a different code or proceed without one.');
      }
      showNotification({ color: 'red', title: 'Payment failed', message });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <Skeleton height={400} m="md" />;
  }

  const paymentMethod = getPatientPaymentMethod(patient);
  const amount = charge?.priceOverride?.value ?? 0;

  return (
    <Stack gap="md" p="md" maw={900} mx="auto" w="100%">
      <Paper p="md" radius="md" withBorder shadow="sm">
        <Stack gap={4}>
          <Text fw={700} size="xs" c={BRAND} style={{ cursor: 'pointer' }} onClick={() => navigate('/my-chart')}>
            {'My Chart >'}
          </Text>
          <Title order={1} fz={32} fw={700}>
            Confirm Payment
          </Title>
        </Stack>
      </Paper>

      <Paper p="md" radius="md" withBorder shadow="sm">
        <Stack gap="md">
          <Title order={3}>Appointment Summary</Title>
          <Group gap="xl">
            <Text>
              <b>Type:</b> {appointment?.appointmentType?.text ?? '—'}
            </Text>
            <Text>
              <b>When:</b> {appointment?.start ? new Date(appointment.start).toLocaleString() : '—'}
            </Text>
          </Group>
        </Stack>
      </Paper>

      <Paper p="md" radius="md" withBorder shadow="sm">
        <Stack gap="md">
          {applied ? (
            <Group justify="space-between">
              <Text c="green" fw={600}>
                Code APPLIED: {applied.code}
              </Text>
              <Button variant="subtle" onClick={handleRemoveCoupon}>
                Remove
              </Button>
            </Group>
          ) : (
            <Stack gap={4}>
              <Group align="end">
                <TextInput
                  label="Coupon code"
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyCoupon();
                    }
                  }}
                  disabled={validating}
                  style={{ flex: 1 }}
                />
                <Button
                  variant="outline"
                  loading={validating}
                  disabled={!code.trim() || validating}
                  onClick={() => {
                    handleApplyCoupon();
                  }}
                >
                  Apply
                </Button>
              </Group>
              {couponError && (
                <Text c="red" size="sm">
                  {couponError}
                </Text>
              )}
            </Stack>
          )}

          {applied ? (
            <Stack gap={4}>
              <Group justify="space-between">
                <Text>Subtotal</Text>
                <Text>{formatUsd(applied.originalAmountCents / 100)}</Text>
              </Group>
              <Group justify="space-between">
                <Text c="green">{`Discount (${applied.code}, ${applied.percentOff}% off)`}</Text>
                <Text c="green">-{formatUsd((applied.originalAmountCents - applied.discountedAmountCents) / 100)}</Text>
              </Group>
              <Group justify="space-between">
                <Title order={3}>Total Due Today</Title>
                <Title order={3}>{formatUsd(applied.discountedAmountCents / 100)}</Title>
              </Group>
            </Stack>
          ) : (
            <Title order={3}>Total Due Today: {formatUsd(amount)}</Title>
          )}

          {paymentMethod ? (
            <Group gap="md">
              <TextInput label="Card brand" value={paymentMethod.brand} disabled />
              <TextInput label="Last 4" value={paymentMethod.last4} disabled />
              <Button
                variant="outline"
                style={{ borderColor: BRAND, color: BRAND }}
                onClick={() => setAddCardOpen(true)}
              >
                Edit via Stripe
              </Button>
            </Group>
          ) : (
            <Group justify="space-between" align="center">
              <Text size="sm">No card on file.</Text>
              <Button style={{ backgroundColor: BRAND }} onClick={() => setAddCardOpen(true)}>
                Add card
              </Button>
            </Group>
          )}
          <Alert color="blue" variant="light">
            Your appointment is held until 2 hours before the start time. After that it will be released if unpaid.
          </Alert>
          <Box>
            <Button
              style={{ backgroundColor: BRAND }}
              loading={submitting}
              disabled={!paymentMethod || submitting}
              onClick={() => handleConfirm()}
            >
              Confirm Payment
            </Button>
          </Box>
        </Stack>
      </Paper>

      {patient.id && (
        <AddCardModal
          opened={addCardOpen}
          patientId={patient.id}
          onClose={() => setAddCardOpen(false)}
          onSaved={(updated) => {
            setPatient(updated);
            setAddCardOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
